import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useMemo } from "react";

export interface SubscriptionPlan {
  id: string;
  plan_key: string;
  display_name: string;
  display_name_hi: string | null;
  price_inr: number;
  original_price_inr: number | null;
  duration_days: number;
  is_active: boolean;
  is_popular: boolean;
  features: string[];
  razorpay_plan_id: string | null;
  created_at: string;
  updated_at: string;
}

export type PlanKey = 'trial' | 'monthly' | 'half_yearly' | 'yearly' | 'three_year' | 'five_year';

/**
 * Single source of truth for subscription plans.
 * Fetches plans from database with realtime updates.
 * Use this instead of hardcoded PLAN_PRICING, PLAN_DURATION_DAYS, PLAN_LABELS.
 */
export function useSubscriptionPlans() {
  const queryClient = useQueryClient();

  const { data: plans, isLoading, error } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('duration_days', { ascending: true });
      
      if (error) throw error;
      return data as SubscriptionPlan[];
    },
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });

  // Realtime subscription for instant updates when admin changes plans
  useEffect(() => {
    const channel = supabase
      .channel('subscription-plans-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'subscription_plans'
        },
        (payload) => {
          console.log('🔄 Subscription plans updated in realtime:', payload);
          // Immediately invalidate and refetch
          queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
          queryClient.refetchQueries({ queryKey: ['subscription-plans'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Memoized lookup maps for efficient access
  const plansByKey = useMemo(() => {
    const map = new Map<string, SubscriptionPlan>();
    plans?.forEach(plan => map.set(plan.plan_key, plan));
    return map;
  }, [plans]);

  // Helper functions - these replace hardcoded constants
  const getPlanPrice = (planKey: PlanKey | string): number => {
    if (planKey === 'trial') return 0;
    return plansByKey.get(planKey)?.price_inr ?? 0;
  };

  const getPlanOriginalPrice = (planKey: PlanKey | string): number | null => {
    if (planKey === 'trial') return null;
    return plansByKey.get(planKey)?.original_price_inr ?? null;
  };

  const getPlanDuration = (planKey: PlanKey | string): number => {
    if (planKey === 'trial') return 30;
    return plansByKey.get(planKey)?.duration_days ?? 30;
  };

  const getPlanLabel = (planKey: PlanKey | string, language: 'en' | 'hi' = 'en'): string => {
    if (planKey === 'trial') return language === 'hi' ? 'ट्रायल' : 'Trial';
    const plan = plansByKey.get(planKey);
    if (!plan) return planKey;
    return language === 'hi' ? (plan.display_name_hi || plan.display_name) : plan.display_name;
  };

  const isPlanPopular = (planKey: PlanKey | string): boolean => {
    return plansByKey.get(planKey)?.is_popular ?? false;
  };

  const getMonthlyEquivalent = (planKey: PlanKey | string): number => {
    const price = getPlanPrice(planKey);
    const days = getPlanDuration(planKey);
    if (days === 0) return 0;
    return Math.round((price / days) * 30);
  };

  const getSavingsVsMonthly = (planKey: PlanKey | string): number => {
    if (planKey === 'monthly' || planKey === 'trial') return 0;
    const monthlyPrice = getPlanPrice('monthly');
    const totalDays = getPlanDuration(planKey);
    const months = totalDays / 30;
    const wouldBeMonthly = monthlyPrice * months;
    return Math.max(0, wouldBeMonthly - getPlanPrice(planKey));
  };

  // Get all active plans for display (excludes trial)
  const activePlans = useMemo(() => {
    return plans?.filter(p => p.plan_key !== 'trial') ?? [];
  }, [plans]);

  // PLAN_PRICING equivalent - dynamic from DB
  const planPricing = useMemo(() => {
    const pricing: Record<string, number> = { trial: 0 };
    plans?.forEach(plan => {
      pricing[plan.plan_key] = plan.price_inr;
    });
    return pricing;
  }, [plans]);

  // PLAN_DURATION_DAYS equivalent - dynamic from DB
  const planDurations = useMemo(() => {
    const durations: Record<string, number> = { trial: 30 };
    plans?.forEach(plan => {
      durations[plan.plan_key] = plan.duration_days;
    });
    return durations;
  }, [plans]);

  // PLAN_LABELS equivalent - dynamic from DB
  const planLabels = useMemo(() => {
    const labels: Record<string, string> = { trial: 'Trial' };
    plans?.forEach(plan => {
      labels[plan.plan_key] = plan.display_name;
    });
    return labels;
  }, [plans]);

  return {
    plans,
    activePlans,
    isLoading,
    error,
    // Lookup helpers
    getPlanPrice,
    getPlanOriginalPrice,
    getPlanDuration,
    getPlanLabel,
    isPlanPopular,
    getMonthlyEquivalent,
    getSavingsVsMonthly,
    // Backward compatible objects (dynamic from DB)
    planPricing,
    planDurations,
    planLabels,
    // Raw map for advanced use
    plansByKey,
  };
}

// Export a context-less version for edge functions (can't use hooks)
export async function fetchSubscriptionPlans() {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('duration_days', { ascending: true });
  
  if (error) throw error;
  return data as SubscriptionPlan[];
}
